import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CONSENT_COOKIE, CONSENT_MAX_AGE_SECONDS, ConsentState, parseConsentCookie } from './consent.types';

/**
 * Holds the visitor's cookie choice and tells the rest of the app which
 * third-party tags may run. Nothing here loads a tag itself — TagConsentService
 * owns loading and reacts to `state$`.
 *
 * The site runs the opt-out model described in the "Cookies and tracking"
 * and "Your choices" sections of the privacy policy: analytics and
 * advertising tags are on for every visitor until they reject them. A
 * Global Privacy Control signal counts as a rejection of `ads` on arrival.
 *
 * The banner only renders on production hostnames: no tag loads on
 * localhost or in CI, so there is nothing to consent to there.
 * `?consent=preview` forces it on for QA.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
    private static readonly PROD_HOSTNAMES = new Set([
        'atmavaan.com',
        'www.atmavaan.com',
    ]);

    private readonly isBrowser: boolean;

    /** True when the browser asked us not to sell or share. */
    readonly gpc: boolean;

    /** True when the banner should be rendered at all. */
    readonly applies: boolean;

    private readonly state$$: BehaviorSubject<ConsentState>;

    /** Emits when something outside the banner asks for the dialog. */
    private readonly openRequests$$ = new Subject<void>();

    /** True once the visitor has accepted, rejected or saved a choice. */
    private decided = false;

    constructor(@Inject(PLATFORM_ID) platformId: Object) {
        this.isBrowser = isPlatformBrowser(platformId);
        this.gpc = this.isBrowser && (navigator as any).globalPrivacyControl === true;
        this.applies = this.isBrowser && this.bannerApplies();

        const stored = this.readCookie();
        if (stored) this.decided = true;
        this.state$$ = new BehaviorSubject<ConsentState>(stored ?? this.defaults());
    }

    /** The footer link (or anything else) asking to open the dialog. */
    get openRequests$(): Observable<void> {
        return this.openRequests$$.asObservable();
    }

    /** Open the preferences dialog from outside the banner component. */
    requestPreferences(): void {
        this.openRequests$$.next();
    }

    /** Emits the current grants, then every change. */
    get state$(): Observable<ConsentState> {
        return this.state$$.asObservable();
    }

    get state(): ConsentState {
        return this.state$$.value;
    }

    /** The banner shows until a choice has been recorded. */
    get needsChoice(): boolean {
        return this.applies && !this.decided;
    }

    /** Opt-out model: everything on, unless the browser sent GPC. */
    defaults(): ConsentState {
        return { analytics: true, ads: !this.gpc };
    }

    /** Record a choice, persist it and tell subscribers. */
    set(next: ConsentState): void {
        const resolved: ConsentState = {
            analytics: next.analytics,
            // A GPC signal is a standing opt-out; the UI cannot override it.
            ads: next.ads && !this.gpc,
        };
        this.decided = true;
        this.writeCookie(resolved);
        this.state$$.next(resolved);
    }

    acceptAll(): void {
        this.set({ analytics: true, ads: true });
    }

    rejectNonEssential(): void {
        this.set({ analytics: false, ads: false });
    }

    private bannerApplies(): boolean {
        if (new URLSearchParams(window.location.search).get('consent') === 'preview') return true;
        return ConsentService.PROD_HOSTNAMES.has(window.location.hostname);
    }

    private readCookie(): ConsentState | null {
        if (!this.isBrowser) return null;
        const stored = parseConsentCookie(document.cookie);
        // A GPC signal is a standing opt-out, whatever was saved before it.
        return stored && { analytics: stored.analytics, ads: stored.ads && !this.gpc };
    }

    private writeCookie(state: ConsentState): void {
        if (!this.isBrowser) return;
        const value = (state.analytics ? 'a' : '-') + (state.ads ? 'x' : '-');
        document.cookie = `${CONSENT_COOKIE}=${value};path=/;max-age=${CONSENT_MAX_AGE_SECONDS};SameSite=Lax`;
    }
}
